/**
 * ArCellRouter Component
 *
 * Routes cell rendering to appropriate control component based on column type.
 * - Entity references → ReferenceSelect
 * - SellPriceUI fields → SellPriceUI (direct)
 * - Other field types → ControlUI
 */

import { CSSProperties, FC, memo, useCallback, useContext } from 'react';
import { UIElementType } from '#types';
import type { ReferencedDocument } from '#types';
import type {
  ArCell as ArCellType,
  ArCellValue,
  ArColumnDefinition,
} from '#types/System.Artifacts.types';
import { FieldTypeContext } from '@/providers/FieldTypeContext';
import ControlUI from '@/components/rendering/ControlUI';
import ReferenceSelect from './ReferenceSelect';
import SellPriceUI from '@/components/rendering/fields/SellPriceUI';
import { CompositeField } from '@/components/CompositeField';

type ArCellRouterProps = {
  column: ArColumnDefinition;
  /** Value to pass to field control. For compound fields, this is { value, composite }. */
  value: ArCellValue;
  /**
   * Callback when cell value changes.
   * For reference columns, `ReferenceSelect` unconditionally fetches the selected record and passes
   * it as `referencedDocument`; `ArCell` feeds it to the row-scoped `elementMapping` pass.
   * Optional for disabled cells that won't trigger changes.
   */
  onChange?: (value: ArCellValue, referencedDocument?: ReferencedDocument | null) => void;
  /** Whether the cell is disabled. */
  disabled?: boolean;
  /** Unique identifier for the cell. */
  cellId: string;
  /** Row document ID (required for composite fields, optional for aggregation cells). */
  rowId?: string;
  /** Handler called when the cell receives focus. */
  onFocus?: () => void;
  /** Handler called when the cell loses focus. */
  onBlur?: () => void;
  /** Optional CSS properties to apply to the cell. */
  style?: CSSProperties;
  /** Whether the cell value is calculated (not directly editable). */
  isCalculated?: boolean;
  /** Display-only mode for aggregations - hides all controls. */
  displayOnly?: boolean;
  /**
   * Native hover line for the control, used by Form Derive projected cells. An
   * antd `Tooltip` cannot serve here: it injects its handlers by cloning the
   * child, and every control below is a `memo` component that drops them.
   */
  title?: string;
  /**
   * The composite sub-fields a Form Derive projection fills in this cell, each
   * mapped to the sentence `title` also carries. Handed to `CompositeField`, which
   * carries them into the sidebar so those sub-fields render disabled — and say
   * why — while the ones beside them stay editable.
   */
  derivedSubFields?: ReadonlyMap<string, string>;
  /**
   * Materializes the row's cell on the live Valtio proxy and returns it, for the
   * sidebar to edit through. Lazy by contract: the owner may not write the proxy
   * during render, so consumers call this only from an event handler.
   */
  resolveCellValueProxy?: () => ArCellType;
};

/**
 * ArCellRouter routes rendering based on column.uiElementType:
 * - UIElementType.Reference with entity → ReferenceSelect
 * - UIElementType.Field → ControlUI
 */
const ArCellRouter: FC<ArCellRouterProps> = ({
  column,
  value,
  onChange,
  disabled = false,
  cellId,
  onFocus,
  onBlur,
  style,
  isCalculated = false,
  displayOnly = false,
  title,
  derivedSubFields,
  resolveCellValueProxy,
}) => {
  const { fieldTypes } = useContext(FieldTypeContext);

  const handleReferenceChange = useCallback(
    (
      newValue: ArCellValue,
      referencedDocument?: ReferencedDocument | null
    ) => onChange?.(newValue, referencedDocument),
    [onChange]
  );

  if (column.uiElementType === UIElementType.Reference) {
    const entityColumn = column;

    if (entityColumn.referenceType !== 'entity' || !entityColumn.entityId) {
      return null;
    }

    return (
      <ReferenceSelect
        key={`${entityColumn.entityId}:${entityColumn.entityGroupId ?? ''}`}
        id={cellId}
        entityId={entityColumn.entityId}
        entityGroupId={entityColumn.entityGroupId}
        value={value}
        disableManualEntry={entityColumn.allowTypeIn === false}
        onChange={handleReferenceChange}
        disabled={disabled}
        style={style}
        title={title}
        onFocus={onFocus}
        onBlur={onBlur}
        resolveCellValueProxy={resolveCellValueProxy}
      />
    );
  }
  const fieldType = fieldTypes[column.typeId];

  // Route SellPriceUI directly
  if (fieldType?.fieldDataTypeId === 'sellPriceUI') {
    const sellPriceLabels = ('sellPriceLabels' in column ? column.sellPriceLabels : undefined)
      ?? fieldType?.sellPriceLabels;
    return (
      <SellPriceUI
        value={value}
        onChange={onChange}
        disabled={disabled}
        displayOnly={displayOnly}
        sellPriceLabels={sellPriceLabels}
        defaultFormulaTypeId={fieldType?.props?.formulaTypeId}
        style={style}
        onFocus={onFocus}
        onBlur={onBlur}
      />
    );
  }

  // Route CompositeField directly
  if (fieldType?.fieldDataTypeId === 'composite') {
    return (
      <CompositeField
        compositeFieldTypeId={column.typeId}
        fieldId={column.typeId}
        value={value}
        onChange={onChange}
        disabled={disabled}
        isFormulaColumn={isCalculated}
        derivedSubFields={derivedSubFields}
        tableMode
        style={style}
        title={title}
        resolveCellValueProxy={resolveCellValueProxy}
      />
    );
  }

  const calculatedStyles = isCalculated ? { 
    input: { 
      fontStyle: 'italic' 
    } 
  } : undefined;

  return (
    <ControlUI
      id={cellId}
      value={value}
      fieldType={fieldType}
      uiField={{
        uiElementType: UIElementType.Field,
        fieldId: column.typeId,
        props: {
          variant: 'borderless',
          style: style,
          styles: calculatedStyles,
          title,
        },
      }}
      onChange={onChange ?? (() => {})}
      onFocus={onFocus}
      onBlur={onBlur}
      readOnly={disabled}
      isCalculated={isCalculated}
      tableMode
      {...fieldType?.props}
    />
  );
};

export default memo(ArCellRouter);
